/* eslint-disable no-console */
/* eslint-disable no-await-in-loop */
import {
  ActivityType,
  ArchivedState,
  Case,
  CaseBySlugQuery,
  CaseFile,
  CaseMilestone,
  CasePhase,
  CaseState,
  Company,
  CompletedCaseMilestoneInput,
  CustomGetCaseQuery,
  CustomListCasesQuery,
  CustomUpdateCaseMutation,
  FileRequirementInput,
  FileType,
  GetCaseQuery,
  ListCaseMilestonesQuery,
  PersonType,
  ProcessRequest,
  ProcessRequestEmployeeData,
  USCISReceiptDetails,
  Role,
  SearchableCaseFilterInput,
  UpdateCaseInput,
  UpdateCaseMutation,
  UserGroup,
  ListUSCISReceiptDetailsQuery,
  VisaStatus,
  ReceiptType,
  CreateUSCISReceiptDetailsMutation,
  UpdateUSCISReceiptDetailsMutation,
  VisaAdditionalProcessing,
} from 'API';
import { Link } from 'react-router-dom';
import {
  CustomListCases,
  caseBySlug, getCase, listCaseFilesPage, listCaseMilestones, updateCase,
} from 'graphql/customCaseQueries';
import * as queries from 'graphql/queries';
import {
  createCaseContributor,
  createCaseFile,
  createUSCISReceiptDetails,
  deleteCaseContributor,
  updateEmployer,
  updateProcessRequest,
  updateTeamMember,
  updateUSCISReceiptDetails,
} from 'graphql/mutations';
import { getEmployer, getTeamMember } from 'graphql/queries';
import ActivityService from 'services/activity';
import AuthService, { MH_ROLES } from 'services/auth';
import FirmService from 'services/firm';
import NationalService, { Relative } from 'services/national';
import PersonService from 'services/person';
import { resolvers } from 'shared/enums';
import labels from 'shared/labels/caseLabels';
import { ROUTES } from 'shared/routes';
import {
  getPayload, lower, notNullFilter, possessive, typeToRole, uniquesByKey,
} from 'shared/utilities';
import { Updater } from 'use-immer';
import { sleep } from 'shared/constants';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';
import { post } from 'aws-amplify/api';

const processResolver = resolvers.VisaStatus;
const subProcessResolver = resolvers.VisaSubCategory;
const visaAdditionalProcessingResolver = resolvers.VisaAdditionalProcessing;

interface RelativeCase {
  case: Case,
  relative: Relative
}

export const CASE_TYPES_FOR_WHICH_COMPANY_PREFS_IS_IRRELEVANT = [
  VisaStatus.E_3D,
  VisaStatus.E_3D_CONSULATE,
  VisaStatus.H_4,
  VisaStatus.H4_CONSULATE,
  VisaStatus.TD,
  VisaStatus.TD_MEXICO,
  VisaStatus.TD_CANADA,
];

class CaseService {
  /**
   * Fetch a case, then drain any file pages AppSync truncated away.
   *
   * `getCase` selects `files` without a limit, so AppSync caps the connection
   * at 100 items. The `byCase` index is sorted by `fileID` — a random UUID —
   * so those 100 are an arbitrary subset rather than the newest or oldest. On
   * a case with more than 100 documents the Documents tab silently dropped the
   * remainder, and a file that had just been uploaded usually wasn't in the
   * page, so it read as if the upload had never landed.
   */
  fetchCaseWithAllFiles = async (
    id: string,
    messagesUserGroup: UserGroup | null,
  ): Promise<Case | null> => {
    let thisCase: Case | undefined;

    try {
      const response = (await client.graphql({
        query: getCase,
        variables: { id, messagesUserGroup },
      })) as GraphQLResult<CustomGetCaseQuery>;

      thisCase = response.data?.getCase as Case | undefined;
    } catch (err: any) {
      thisCase = err?.data?.getCase as Case | undefined;

      if (!thisCase) {
        throw err;
      }
    }

    if (!thisCase?.files) {
      return thisCase ?? null;
    }

    let nextToken = thisCase.files.nextToken ?? null;

    // Drain best-effort: a page that fails must not discard the case we already
    // hold, and a partial page is still better than dropping its rows entirely.
    while (nextToken) {
      let moreFiles: any;

      try {
        const page = (await client.graphql({
          query: listCaseFilesPage,
          variables: { id, nextToken },
        })) as GraphQLResult<any>;

        moreFiles = page.data?.getCase?.files;
      } catch (err: any) {
        moreFiles = err?.data?.getCase?.files;

        if (!moreFiles) {
          console.log('fetchCaseWithAllFiles: stopped paging case files early', err);
          break;
        }
      }

      thisCase.files.items.push(...(moreFiles?.items || []));
      nextToken = moreFiles?.nextToken ?? null;
    }

    thisCase.files.items = this.uniqueByFile(thisCase.files.items.filter(this.fileBelongsToCase(thisCase)));

    return thisCase;
  };

  /**
   * A file records the national it belongs to in `ownerNationalID`. Uploading a
   * company-level document used to link it to every open case at that company
   * (dynamoTrigger/cleanupFileConnections), so cases carry CaseFile rows for
   * other people's personal documents — invisible on the Documents tab, but the
   * "Download all as ZIP" action shipped them to whoever asked.
   *
   * Those rows are left in place and filtered out on read instead: deleting live
   * connection rows is far riskier than ignoring them. A file with no
   * `ownerNationalID` is a genuine company or case document and passes through.
   *
   * The whole family passes, not only the case's own national: a dependent case
   * legitimately carries the primary's documents and vice versa. `relativeAdmins`
   * is one-directional — it lists who may administer this national, not who they
   * administer — so `relatives` is included as well.
   */
  /**
   * Re-apply the owner guard to a whole case object.
   *
   * Filtering inside `fetchCaseWithAllFiles` is not enough on its own: the
   * `updateCase` mutation selects the entire `files` connection, so dropping a
   * raw mutation response into CaseContext — which a milestone, receipt number,
   * document type or case edit all do — put the unfiltered list back on screen
   * and into the ZIP. Every whole-case assignment goes through this.
   */
  withOwnedFiles = (thisCase: Case | null): Case | null => {
    if (!thisCase?.files?.items) {
      return thisCase;
    }

    return {
      ...thisCase,
      files: {
        ...thisCase.files,
        items: this.uniqueByFile(thisCase.files.items.filter(this.fileBelongsToCase(thisCase))),
      },
    };
  };

  fileBelongsToCase = (thisCase: Case) => {
    const family = new Set<string>([
      thisCase.foreignNationalID,
      ...(thisCase.relativeAdmins || []),
      ...(thisCase.foreignNational?.relativeAdmins || []),
      ...(thisCase.foreignNational?.relatives || []),
    ]);

    return (item: CaseFile | null): boolean => {
      const ownerID = item?.file?.ownerNationalID;

      if (!ownerID) {
        return true;
      }

      return family.has(ownerID);
    };
  };

  /**
   * One FileData can be linked to a case by more than one CaseFile row: the
   * upload card and dynamoTrigger both created a connection with a random id
   * (VOYAG-803), so the Documents tab showed the document twice. New links use
   * a deterministic id; this hides the duplicates already in the table. The case
   * query selects the nested file.id, not always the connection-level fileID.
   */
  uniqueByFile = (items: Array<CaseFile | null>): Array<CaseFile | null> => {
    const seen = new Set<string>();

    return items.filter((item) => {
      const fileID = item?.file?.id || item?.fileID;

      if (!fileID) {
        return true;
      }

      if (seen.has(fileID)) {
        return false;
      }

      seen.add(fileID);

      return true;
    });
  };

  // Keep in sync with gqlCreateCaseFile in dynamoTrigger/src/graphQLConnect.js.
  caseFileID = (caseID: string, fileID: string): string => `${caseID}_${fileID}`;

  /**
   * Link a file to a case. Idempotent: a second create for the same pair (from
   * dynamoTrigger, or a repeat click) fails the resolver's id-uniqueness
   * condition, which means the link already exists — not an error.
   */
  linkFileToCase = async (caseID: string, fileID: string): Promise<void> => {
    try {
      await client.graphql({
        query: createCaseFile,
        variables: {
          input: {
            id: this.caseFileID(caseID, fileID),
            caseID,
            fileID,
          },
        },
      });
    } catch (err: any) {
      const alreadyLinked = err?.errors?.some(
        (error: any) => String(error?.errorType || '').includes('ConditionalCheckFailed'),
      );

      if (!alreadyLinked) {
        throw err;
      }
    }
  };

  addCaseContributor = async (
    thisCase: Case,
    contributor: Person,
    role: Role,
  ): Promise<Case> => {
    const messagesUserGroup = AuthService.roleToUserGroup(role);
    const personID = PersonService.getPersonID();
    const personType = PersonService.getPersonType();

    try {
      await client.graphql({
        query: createCaseContributor,
        variables: {
          input: {
            caseID: thisCase.id,
            contributorID: contributor.id,
            foreignNationalID: thisCase.foreignNationalID,
            state: thisCase.state,
            type: contributor.type,
          },
        },
      });

      const updatedCase = await this.fetchCaseWithAllFiles(thisCase.id, messagesUserGroup);

      if (updatedCase) {
        await ActivityService.createActivity({
          activityType: ActivityType.CASE_TEAM_CONTRIBUTOR_ADDED,
          actorID: personID,
          actorType: personType,
          directTargetID: contributor.id,
          ownerID: thisCase.id!,
        }, []);

        return updatedCase;
      }

      throw new Error();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  completeMilestone = async (
    thisCase: Case,
    milestone: CompletedCaseMilestoneInput,
    role: Role,
  ): Promise<Case> => {
    try {
      const caseResponse = (await client.graphql({
        query: listCaseMilestones,
        variables: { id: thisCase.id },
      })) as GraphQLResult<ListCaseMilestonesQuery>;

      const currentMilestones = (caseResponse.data?.getCase
        ?.completedMilestones || []) as Array<CompletedCaseMilestoneInput>;

      if (currentMilestones.some((m) => m.milestone === milestone.milestone)) {
        throw new Error('Milestone already completed');
      }

      const updateResponse = (await client.graphql({
        query: updateCase,
        variables: {
          input: {
            id: thisCase.id,
            completedMilestones: [...currentMilestones, milestone],
          },
          messagesUserGroup: AuthService.roleToUserGroup(role),
        },
      })) as GraphQLResult<CustomUpdateCaseMutation>;

      if (updateResponse.data?.updateCase) {
        const recipients = [
          ...this.getCaseTeamIDs(thisCase!),
          ...((thisCase?.foreignNational?.relativeAdmins) || []),
        ];
        await ActivityService.createActivity({
          activityType: ActivityType.CASE_MILESTONE_COMPLETED,
          actorID: PersonService.getPersonID(),
          actorType: PersonService.getPersonType(),
          targetString: milestone.milestone,
          ownerID: thisCase.id!,
        }, recipients);

        return updateResponse.data.updateCase as Case;
      }

      throw new Error();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  getLabel = (fieldName: string): string => labels[fieldName] || '';

  removeMilestone = async (
    thisCase: Case,
    milestone: CaseMilestone,
    role: Role,
  ) => {
    try {
      const caseResponse = (await client.graphql({
        query: listCaseMilestones,
        variables: { id: thisCase.id },
      })) as GraphQLResult<ListCaseMilestonesQuery>;

      const currentMilestones = (caseResponse.data?.getCase
        ?.completedMilestones || []) as Array<CompletedCaseMilestoneInput>;

      const updatedMilestones = currentMilestones.filter(
        (m) => m.milestone !== milestone,
      );
      const updateResponse = (await client.graphql({
        query: updateCase,
        variables: {
          input: {
            id: thisCase.id,
            completedMilestones: updatedMilestones,
          },
          messagesUserGroup: AuthService.roleToUserGroup(role),
        },
      })) as GraphQLResult<UpdateCaseMutation>;

      if (updateResponse.data?.updateCase) {
        const recipients = [
          ...this.getCaseTeamIDs(thisCase!),
          ...((thisCase?.foreignNational?.relativeAdmins) || []),
        ];
        await ActivityService.createActivity({
          activityType: ActivityType.CASE_MILESTONE_UNCOMPLETED,
          actorID: PersonService.getPersonID(),
          actorType: PersonService.getPersonType(),
          targetString: milestone,
          ownerID: thisCase.id!,
        }, recipients);

        return updateResponse.data.updateCase as Case;
      }

      throw new Error();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  requestDocumentUpload = async (
    thisCase: Case,
    setThisCase: Updater<Case | null>,
    fileType: FileType | string,
    isCustomRequirement: boolean,
  ): Promise<string> => {
    const actorType = PersonService.getPersonType();
    const role = typeToRole(actorType);
    const actorID = PersonService.getPersonID();
    const currentFileRequirements = thisCase.fileRequirements || [];
    const type = isCustomRequirement ? 'fileType' : 'type';
    const fileRequirement = currentFileRequirements.find(
      (requirement) => requirement[type] === fileType,
    );
    const directTargetID = fileRequirement?.assignedToID;
    const isLegalAssistants = fileRequirement?.isAssignedToLegalAssistants;

    if (!directTargetID && !isLegalAssistants) {
      throw new Error();
    }

    // 1. First, set this requirements isRequested value to true
    // and remove approval (if present)
    const updatedFileRequirement = {
      ...fileRequirement,
      isApproved: false,
      isRequested: true,
    };

    const updatedFileRequirements = [
      ...currentFileRequirements.filter((req) => req[type] !== fileType),
      updatedFileRequirement,
    ];

    const updatedCase = await this.updateCase(
      {
        id: thisCase.id!,
        fileRequirements: updatedFileRequirements as Array<FileRequirementInput>,
      },
      role,
    );

    // 2. Update the state
    setThisCase(updatedCase);
    // 3. Create an activity and notify the assignee.

    await ActivityService.createActivity({
      activityType: ActivityType.DOCUMENT_UPLOAD_REQUESTED,
      targetString: isLegalAssistants ? 'legal assistants' : undefined,
      directTargetID: directTargetID || undefined,
      actorID,
      actorType,
      indirectTargetString: fileType,
      ownerID: thisCase.id!,
    }, isLegalAssistants
      ? await FirmService.listLegalAssistantIDs()
      : [directTargetID as string]);

    // 4. Return the assignee, so that a success message can display.
    const caseTeam = this.getCaseTeam(thisCase);

    const possibleAssignees = [...caseTeam, thisCase.foreignNational];

    const assignee = possibleAssignees.find(
      (person) => person.id === directTargetID,
    );

    return isLegalAssistants
      ? 'The legal assistants have been notified of the request.'
      : assignee
        ? `${PersonService.fullname(assignee)} has been notified of the request.`
        : '';
  };

  updateMilestone = async (
    caseID: string,
    updatedMilestone: CompletedCaseMilestoneInput,
    role: Role,
  ): Promise<Case> => {
    try {
      const caseResponse = (await client.graphql({
        query: listCaseMilestones,
        variables: { id: caseID },
      })) as GraphQLResult<ListCaseMilestonesQuery>;

      const currentMilestones = (caseResponse.data?.getCase
        ?.completedMilestones || []) as Array<CompletedCaseMilestoneInput>;

      const updatedMilestones = [
        ...currentMilestones.filter(
          (m) => m.milestone !== updatedMilestone.milestone,
        ),
        updatedMilestone,
      ];

      const updateResponse = (await client.graphql({
        query: updateCase,
        variables: {
          input: {
            id: caseID,
            completedMilestones: updatedMilestones,
          },
          messagesUserGroup: AuthService.roleToUserGroup(role),
        },
      })) as GraphQLResult<UpdateCaseMutation>;

      if (updateResponse.data?.updateCase) {
        return updateResponse.data.updateCase as Case;
      }

      throw new Error();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  getCaseLiveStatus = async (caseID: string, receiptType: ReceiptType | null): Promise<any> => {
    try {
      const payload = await getPayload({ caseID, type: receiptType });
      const { body: responseBody } = await post({ apiName: 'ThirdParty', path: '/uscis-case-status', options: payload as any }).response;
      const response = await responseBody.json() as any;
      if (!response) {
        throw new Error('Something went wrong getting this case live status.');
      }

      return response;
    } catch (err: any) {
      throw new Error(err.response?.data?.message);
    }
  };

  create = async (
    process: ProcessRequest,
  ): Promise<Case> => {
    if (!process.foreignNationalID) {
      const employeeData = process.employeeData as ProcessRequestEmployeeData;
      let existingNationalId: string | null = null;

      // If email exists, check for existing FN with same email to avoid duplicate
      if (employeeData.email) {
        try {
          const existingPerson = await PersonService.getByEmail(employeeData.email);
          if (existingPerson?.type === PersonType.ForeignNational) {
            existingNationalId = existingPerson.id;
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('Could not lookup existing FN by email, will create new:', err);
        }
      }

      if (existingNationalId) {
        await client.graphql({
          query: updateProcessRequest,
          variables: {
            input: { id: process.id, foreignNationalID: existingNationalId },
          },
        });
      } else {
        const slug = await PersonService
          .generateSlug(`${employeeData.firstname} ${employeeData.lastname}`);

        const nationalInput = {
          id: employeeData.tempID,
          firstname: employeeData.firstname,
          lastname: employeeData.lastname,
          employeeId: employeeData.employeeId,
          lowerFirst: lower(employeeData.firstname!),
          lowerLast: lower(employeeData.lastname!),
          lowerFull: lower(`${employeeData.lastname} ${employeeData.firstname}`),
          spouse: employeeData.spouse,
          dependents: employeeData.dependents,
          ...(employeeData.email ? { email: employeeData.email } : {}),
          ...(process.companyID
            ? { companyID: process.companyID }
            : {}),
          slug,
          type: PersonType.ForeignNational,
          fileRequirements: [],
          archived: ArchivedState.ACTIVE,
        };

        const newNational = await PersonService.create(
          nationalInput, Role.FOREIGNNATIONAL,
        );

        await client.graphql({
          query: updateProcessRequest,
          variables: {
            input: { id: process.id, foreignNationalID: newNational.id },
          },
        });
      }
    }

    const payload = await getPayload({ processID: process.id });

    try {
      const { body: caseBody } = await post({ apiName: 'utilitiesapi', path: '/cases', options: payload as any }).response;
      const response = await caseBody.json() as any;
      if (!response.case) {
        throw new Error('Something went wrong creating this case.');
      }

      return response.case;
    } catch (err: any) {
      throw new Error(err.response?.data?.message);
    }
  };

  /**
   *
   * @param c The case
   * @param personalize Whether to use “your” when case belongs to current user
   * @param actorID ID of the actor (relevant for activities)
   * @returns {string} Either “John Smith’s H-1B case” or “Your H-1B case”
   */
  fullname = (c: Case, personalize?: boolean, actorID?: string): string => {
    const personID = PersonService.getPersonID();

    const caseName = `${c.requestedProcess
      ? processResolver(c.requestedProcess)
      : ''} case`;

    if (personalize && personID === c.foreignNationalID) {
      return `your ${caseName}`;
    }

    if (personalize && actorID === c.foreignNationalID) {
      return `their ${caseName}`;
    }

    return `${possessive(PersonService.fullname(c.foreignNational))} ${caseName}`;
  };

  getBySlug = async (slug: string, role: Role): Promise<Case | null> => {
    let messagesUserGroup = null;
    if (MH_ROLES.includes(role)) {
      messagesUserGroup = UserGroup.Team;
    } else if (role === Role.EMPLOYER) {
      messagesUserGroup = UserGroup.Employers;
    } else if (role === Role.FOREIGNNATIONAL) {
      messagesUserGroup = UserGroup.Nationals;
    }

    try {
      const slugResponse = (await client.graphql({
        query: caseBySlug,
        variables: { slug },
      })) as GraphQLResult<CaseBySlugQuery>;

      if (!slugResponse.data?.caseBySlug?.items![0]) {
        return null;
      }

      const { id } = slugResponse.data.caseBySlug.items[0];

      const thisCase = await this.fetchCaseWithAllFiles(id, messagesUserGroup);

      return thisCase;
    } catch (err: any) {
      if (err.data?.getCase) {
        return err.data.getCase;
      }

      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  /** Get more than one case at once. */
  getCases = async (
    caseIDs: Array<string>,
    operation?: string,
  ): Promise<Array<Case>> => {
    try {
      const cases = await Promise.all(
        caseIDs.map(async (id) => {
          const response = (await client.graphql({
            query: operation || queries.getCase,
            variables: { id },
          })) as GraphQLResult<GetCaseQuery>;

          return response?.data?.getCase;
        }),
      );

      return cases.filter(notNullFilter) as Array<Case>;
    } catch (err) {
      throw new Error();
    }
  };

  getCaseTeam = (thisCase: Case): Array<Person> => uniquesByKey([
    thisCase.attorney,
    thisCase.caseManager,
    thisCase.sponsoringEmployer,
    thisCase.sponsoringNational,
    ...(thisCase.contributors?.items?.filter(notNullFilter)
      .map(
        (item) => item!.contributorEmployer
          || item!.contributorForeignNational
          || item!.contributorTeamMember
          || null,
      ) || []),
  ].filter(notNullFilter)) as Array<Person>;

  getCaseTeamIDs = (thisCase: Case): Array<string> => [
    thisCase.attorneyID,
    thisCase.caseManagerID,
    thisCase.sponsoringEmployerID,
    thisCase.sponsoringNationalID,
    ...(thisCase.contributors?.items?.filter(notNullFilter)
      .map(
        (item) => item!.contributorID,
      ) || []),
  ].filter(notNullFilter) as Array<string>;

  getDocumentAssignee = (thisCase: Case, fileType: FileType):string | null => {
    const requirements = thisCase.fileRequirements || [];

    const requirement = requirements.find((req) => req.type === fileType);

    return requirement?.assignedToID || null;
  };

  getDocumentCount = (thisCase?: Case | null): CompletionCount => {
    if (!thisCase) {
      return { completed: 0, total: 0 };
    }

    const requirements = thisCase.fileRequirements || [];

    const completed = requirements
      .filter((requirement) => requirement.type !== FileType.OTHER)
      .filter((requirement) => requirement.isApproved).length;

    const total = requirements.filter(
      (requirement) => requirement.type !== FileType.OTHER,
    ).length;

    return { completed, total };
  };

  getFormCount = (thisCase?: Case | null): CompletionCount => {
    if (!thisCase) {
      return { completed: 0, total: 0 };
    }

    const total = (thisCase.preparedForms?.items || []).length;
    const completed = (thisCase.preparedForms?.items || []).filter(
      (item) => item?.finalCaseManagerApproverID,
    ).length;

    return {
      completed,
      total,
    };
  };

  // just return the mapped value of the process type
  getProcessTypeMappedText = (thisCase : Case) : string => {
    if (thisCase && thisCase.requestedProcess) {
      return processResolver(thisCase.requestedProcess) || '';
    }

    return '';
  };

  getFullProcessText = (thisCase: Case): string | null => {
    if (!thisCase.requestedProcess) {
      return null;
    }

    let text = this.getProcessTypeMappedText(thisCase);

    if (thisCase?.requestedProcessSubCategory) {
      text += ` ${subProcessResolver(thisCase.requestedProcessSubCategory)}`;
    } else if (thisCase?.globalRequestedProcessSubCategory) {
      text += ` ${thisCase.globalRequestedProcessSubCategory}`;
    }

    if (Array.isArray(thisCase?.requestedProcessAdditionalProcessing) && thisCase?.requestedProcessAdditionalProcessing.length > 0) {
      text += ` - ${thisCase.requestedProcessAdditionalProcessing.map((e) => visaAdditionalProcessingResolver(e as VisaAdditionalProcessing)).join(' + ')}`;
    }

    return text;
  };

  /**
   *
   * Given a case, return the list of its phases. For each phase, examine the
   * case’s list of completed milestones to determine whether the phase is
   * complete and whether it is the current phase.
   *
   * @param {Case} thisCase
   */
  getPhaseStatus = (
    thisCase: Case,
  ): Array<{
    phase: CasePhase;
    isCurrent: boolean;
    isComplete: boolean;
  }> => {
    if (!thisCase?.phaseRequirements) {
      return [];
    }

    const returnArray = thisCase.phaseRequirements!.map((phase) => {
      // A phase is complete if it contains no milestones
      // that are not among the case’s completed milestones.
      const isComplete = !phase!.milestones!.some(
        (milestone) => !(thisCase!.completedMilestones || [])
          .map((completedMilestone) => completedMilestone.milestone)
          .includes(milestone),
      );

      return {
        phase: phase!.phase!,
        isComplete,
        isCurrent: false,
      };
    });

    // Now that we've noted which phases are complete, find the current one.
    // It will be the phase that matches thisCase.currentPhase. Or, if
    // currentPhase is null, it's the first one that is incomplete.

    let indexOfCurrent;

    if (thisCase.currentPhase) {
      indexOfCurrent = returnArray.findIndex(
        (phase) => thisCase.currentPhase === phase.phase,
      );
    } else {
      indexOfCurrent = returnArray.findIndex(
        (phase) => !phase.isComplete,
      );
    }

    if (indexOfCurrent > -1 && indexOfCurrent < returnArray.length) {
      returnArray[indexOfCurrent].isCurrent = true;
    }

    return returnArray;
  };

  /**
   *
   * @param thisCase
   * @returns {Array<RelativeCase>} An array of cases for relatives of a case’s
   *                                foreign national, sorted from newest to
   *                                oldest.
   */
  getRelativesCases = (thisCase?: Case | null):Array<RelativeCase> => {
    if (!thisCase) {
      return [];
    }
    const cases: Array<RelativeCase> = [];

    const asSource = (thisCase
      .foreignNational!.relativesAsSource?.items?.filter(notNullFilter)
      .filter(
        (item) => item?.target?.cases?.items
          && item.target.cases.items.length > 0,
      )
      .map((item: any) => ({
        ...item?.target,
        isSource: false,
        relation: NationalService.getRelationshipLabel(
          item!.type || null,
          true,
        ),
        relationshipID: item.id,
        relationshipType: item!.type,
      })) || []) as Array<Relative>;

    const asTarget = (thisCase
      .foreignNational!.relativesAsTarget?.items?.filter(notNullFilter)
      .filter(
        (item) => item?.source?.cases?.items
          && item.source.cases.items.length > 0,
      )
      .map((item: any) => ({
        ...item?.source,
        isSource: true,
        relation: NationalService.getRelationshipLabel(
          item!.type || null,
          false,
        ),
        relationshipID: item.id,
        relationshipType: NationalService.getInverseRelationship(item!.type),
      })) || []) as Array<Relative>;

    const relativesWithCases = [...asTarget, ...asSource];

    relativesWithCases.forEach((relative) => {
      relative!.cases!.items!.forEach((c) => {
        if (c) {
          cases.push({
            case: c,
            relative,
          });
        }
      });
    });

    return cases.sort((a, b) => {
      const dateA = a.case.startDate || '';
      const dateB = b.case.startDate || '';

      return dateA < dateB ? 1 : -1;
    });
  };

  link = (c: Case) => `${ROUTES.cases}/${c.slug}`;

  /**
   *
   * @param c The case
   * @param personalize Whether to use “your” in text
   * @param actorID ID of the actor (relevant for activities)
   * @returns Link to the case
   */
  linkName = (c: Case, personalize?: boolean, actorID?: string) => (
    <Link
      to={this.link(c)}
    >
      {this.fullname(c, personalize, actorID)}
    </Link>
  );

  recentlyViewedCacheKey = 'recently-viewed-cases';

  /**
   * Remove a contributor from a case.
   *
   * @param {string} caseContributorID — The ID of the CaseContributor —
   * the connection between Case and a Person — not the Contributor itself.
   * @param {string} caseID
   * @param {Role} role - The Role of the person performing this action.
   * @returns
   */
  removeCaseContributor = async (
    caseContributorID: string,
    caseID: string,
    role: Role,
  ): Promise<Case> => {
    const messagesUserGroup = AuthService.roleToUserGroup(role);

    try {
      await client.graphql({
        query: deleteCaseContributor,
        variables: {
          input: {
            id: caseContributorID,
          },
        },
      });

      const updatedCase = await this.fetchCaseWithAllFiles(caseID, messagesUserGroup);

      if (updatedCase) {
        return updatedCase;
      }

      throw new Error();
    } catch {
      throw new Error();
    }
  };

  trackView = async (thisCase: Case, person: Person) => {
    const MAX_CASES = 10;
    const userID = person.id;
    const userType = person.__typename;
    if (
      userType !== PersonType.TeamMember
      && userType !== PersonType.Employer
    ) {
      return;
    }

    let query: any;
    let mutation: any;

    if (userType === PersonType.TeamMember) {
      query = getTeamMember;
      mutation = updateTeamMember;
    } else if (userType === PersonType.Employer) {
      query = getEmployer;
      mutation = updateEmployer;
    }

    try {
      const response = ((await client.graphql({
        query: query,
        variables: { id: userID },
      })) as any)?.data[`get${userType}`];

      if (!response) {
        throw new Error();
      }

      const currentViews = (response.recentlyViewedCases || []).filter(
        (id: string) => id !== thisCase.id,
      );

      const updatedViews = [thisCase.id, ...currentViews].slice(0, MAX_CASES);

      const currentCache = localStorage.getItem(this.recentlyViewedCacheKey)
        ? JSON.parse(localStorage.getItem(this.recentlyViewedCacheKey)!)
        : [];

      const updatedCache = [
        {
          id: thisCase.id,
          slug: thisCase.slug,
          caseNumber: thisCase.caseNumber,
          requestedProcess: thisCase.requestedProcess,
          caseManagerID: thisCase.caseManagerID,
          companyID: thisCase.companyID,
          company: {
            id: thisCase.company?.id,
            name: thisCase.company?.name,
          },
          foreignNationalID: thisCase.foreignNationalID,
          foreignNational: {
            id: thisCase.foreignNational!.id,
            firstname: thisCase.foreignNational!.firstname,
            lastname: thisCase.foreignNational!.lastname,
            slug: thisCase.foreignNational!.slug,
            immigrationStatus: thisCase.foreignNational!.immigrationStatus,
          },
          phaseRequirements: thisCase.phaseRequirements,
          completedMilestones: thisCase.completedMilestones,
        },
        ...currentCache.filter((c:Case) => c.id !== thisCase.id),
      ];

      localStorage.setItem(
        this.recentlyViewedCacheKey, JSON.stringify(updatedCache),
      );

      await client.graphql({
        query: mutation,
        variables: {
          input: { id: userID, recentlyViewedCases: updatedViews },
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  };

  /**
   * @param {UpdateCaseInput} input
   * @param {Role} role - The role of the person making the update
   * @returns {Case} An updated case that can be used to update Case state.
   */
  updateCase = async (input: UpdateCaseInput, role: Role): Promise<Case> => {
    const messagesUserGroup = AuthService.roleToUserGroup(role);

    try {
      const updateResponse = (await client.graphql({
        query: updateCase,
        variables: {
          input,
          messagesUserGroup,
        },
      })) as GraphQLResult<CustomUpdateCaseMutation>;

      if (updateResponse?.data?.updateCase) {
        return updateResponse.data.updateCase as Case;
      }

      throw new Error();
    } catch (err) {
      // AppSync returns GraphQL errors as a thrown rejection even when the
      // mutation itself succeeded and `data` is present — e.g. a non-fatal
      // serialization error on an unrelated nested field (a stale enum value
      // on one of the foreign national's other cases). The write persisted, so
      // treat partial success as success and use the returned data; otherwise
      // the save silently "fails" in the UI and the change only appears after
      // a full page reload.
      const partial = err as { data?: CustomUpdateCaseMutation } | null;
      if (partial?.data?.updateCase) {
        return partial.data.updateCase as Case;
      }
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  getCaseTeamIDsExceptEM = (thisCase: Case): Array<string> => [
    thisCase.attorneyID,
    thisCase.caseManagerID,
    thisCase.sponsoringNationalID,
    ...(thisCase.contributors?.items?.filter(notNullFilter)
      .filter((contributor) => contributor?.contributorEmployer === null) // Dont want to notify EM
      .map(
        (item) => item!.contributorID,
      ) || []),
  ].filter(notNullFilter) as Array<string>;

  /* Get all Active Cases */

  getActiveCases = async (isMH: boolean, employerID: string, employerCompanies: Company[]) => {
    const casesArray: Case[] = [];
    let nextToken = null;
    const limit = 1000;

    try {
      if (isMH) {
        do {
          const response = await (client.graphql({
            query: CustomListCases,
            variables: {
              filter: { state: { eq: CaseState.OPEN } },
              limit,
              nextToken,
            },
          })) as GraphQLResult<CustomListCasesQuery>;

          await sleep(1000);

          if (response.data?.listCases) {
            const { items } = response.data.listCases;
            nextToken = response.data.listCases.nextToken;

            casesArray.push(...(items as Case[] || []));
          }
        } while (nextToken);
      } else if (employerID && employerCompanies?.length) {
        const filter: SearchableCaseFilterInput = { or: [] };
        filter.or = employerCompanies.map(({ id }) => ({ companyID: { eq: id }, state: { eq: CaseState.OPEN } }));

        do {
          const response = await (client.graphql({
            query: CustomListCases,
            variables: {
              filter,
              limit,
              nextToken,
            },
          })) as GraphQLResult<CustomListCasesQuery>;

          if (response.data?.listCases) {
            const { items } = response.data.listCases;
            nextToken = response.data.listCases.nextToken;

            casesArray.push(...(items as Case[] || []));
          }
        } while (nextToken);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('ERROR >>>>>', error);
    }

    return casesArray?.length;
  };

  getCaseReceiptDetails = async (thisCase: Case, receiptNumber: string) => {
    const receiptDetailsArray: USCISReceiptDetails[] = [];
    let nextToken = null;
    const limit = 100;

    try {
      const receiptNumbers = receiptNumber.includes(',')
        ? receiptNumber.split(',').map((num) => num.trim().replace(/-/g, ''))
        : [receiptNumber.replace(/-/g, '')];

      do {
        const response = await (client.graphql({
          query: queries.listUSCISReceiptDetails,
          variables: {
            filter: {
              caseID: { eq: thisCase?.id },
              isActive: { eq: true },
              or: receiptNumbers.map((receiptNo) => ({
                receiptNumber: { eq: receiptNo },
              })),
            },
            limit,
            nextToken,
          },
        })) as GraphQLResult<ListUSCISReceiptDetailsQuery>;

        if (response.data?.listUSCISReceiptDetails) {
          const { items } = response.data.listUSCISReceiptDetails;
          nextToken = response.data.listUSCISReceiptDetails.nextToken;

          receiptDetailsArray.push(...(items as USCISReceiptDetails[] || []));
        }
      } while (nextToken);

      return receiptDetailsArray;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('ERROR >>>>>', error);
      throw new Error();
    }
  };

  deleteCaseReceiptDetail = async (receiptDetailId: string) => {
    try {
      const updateResponse = await (client.graphql({
        query: updateUSCISReceiptDetails,
        variables: {
          input: { id: receiptDetailId, isActive: false },
        },
      })) as GraphQLResult<UpdateUSCISReceiptDetailsMutation>;

      if (updateResponse.data?.updateUSCISReceiptDetails) {
        return updateResponse.data.updateUSCISReceiptDetails;
      }

      throw new Error();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('ERROR >>>>>', error);
      throw new Error();
    }
  };

  deleteCaseReceiptDetailsByReceiptNumber = async (receiptNumbers: string[], caseID: string) => {
    try {
      let nextToken = null;
      const filter = {
        caseID: { eq: caseID },
        isActive: { eq: true },
        or: receiptNumbers.map((receiptNo) => ({
          receiptNumber: { eq: receiptNo },
        })),
      };
      const limit = 100;
      const receiptDetailsArray: USCISReceiptDetails[] = [];
      do {
        const listResponse = (await client.graphql({
          query: queries.listUSCISReceiptDetails,
          variables: {
            filter,
            limit,
            nextToken,
          },
        })) as GraphQLResult<ListUSCISReceiptDetailsQuery>;

        await sleep(1000);

        if (listResponse?.data?.listUSCISReceiptDetails) {
          const { items } = listResponse.data.listUSCISReceiptDetails;
          nextToken = listResponse.data.listUSCISReceiptDetails.nextToken;

          receiptDetailsArray.push(...(items as USCISReceiptDetails[] || []));
        } else {
          return null;
        }
      } while (nextToken);

      const updatePromises = receiptDetailsArray.map(async (detail) => {
        const updateResponse = await this.deleteCaseReceiptDetail(detail?.id as string);

        return updateResponse;
      });

      const updatedRecords = await Promise.all(updatePromises);

      return updatedRecords;
    } catch (error) {
      console.log('ERROR >>>>>', error);
      throw error instanceof Error ? error : new Error('Unknown error occurred');
    }
  };

  createCaseStatusManually = async (caseID: string, manualReceiptStatus: USCISReceiptDetails, latestReceiptStatus: USCISReceiptDetails | null) => {
    try {
      const {
        currentCaseStatusText,
        currentCaseStatusDesc,
        receiptNumber,
        receiptType,
      } = manualReceiptStatus;
      const createResponse = await (client.graphql({
        query: createUSCISReceiptDetails,
        variables: {
          input: {
            caseID, isActive: true, isLatest: true, receiptNumber, receiptType, currentCaseStatusText, currentCaseStatusDesc,
          },
        },
      })) as GraphQLResult<CreateUSCISReceiptDetailsMutation>;
      if (createResponse.data?.createUSCISReceiptDetails) {
        if (latestReceiptStatus) {
          const updateResponse = await (client.graphql({
            query: updateUSCISReceiptDetails,
            variables: {
              input: { id: latestReceiptStatus.id, isLatest: false },
            },
          })) as GraphQLResult<UpdateUSCISReceiptDetailsMutation>;
          if (!updateResponse.data?.updateUSCISReceiptDetails) {
            throw new Error();
          }
        }
      } else {
        throw new Error();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('ERROR >>>>>', error);
      throw new Error();
    }
  };

  getLatestReceiptStatus = async (caseID: string, receiptNumber: string, receiptType: ReceiptType) => {
    const receiptDetailsArray: USCISReceiptDetails[] = [];
    let nextToken = null;
    const limit = 100;

    try {
      do {
        const response = await (client.graphql({
          query: queries.listUSCISReceiptDetails,
          variables: {
            filter: {
              caseID: { eq: caseID },
              isActive: { eq: true },
              isLatest: { eq: true },
              receiptNumber: { eq: receiptNumber },
              receiptType: { eq: receiptType },
            },
            limit,
            nextToken,
          },
        })) as GraphQLResult<ListUSCISReceiptDetailsQuery>;

        if (response.data?.listUSCISReceiptDetails) {
          const { items } = response.data.listUSCISReceiptDetails;
          nextToken = response.data.listUSCISReceiptDetails.nextToken;
          receiptDetailsArray.push(...(items as USCISReceiptDetails[] || []));
        }
      } while (nextToken);

      if (receiptDetailsArray?.length > 0) {
        return receiptDetailsArray[0];
      }

      return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('ERROR >>>>>', error);
      throw new Error();
    }
  };
}

export default new CaseService();
